import {ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Divider,
} from '@mui/material';
import {
  acsIntegrationClient,
  PdkReconcileCreateRow,
  PdkReconcileDeleteRow,
  PdkReconcilePreview,
  PdkReconcileUpdateRow,
} from 'src/apis/acsIntegrationClient';
import {roomServiceFind} from 'src/apis';
import message from 'src/components/Message';

type BuildingLite = {id: string; name: string; apartmentId?: string};
type ApartmentLite = {id: string; name: string};

type CreateDraft = {
  apply: boolean;
  apartmentId: string;
  buildingId: string;
  unitNumber: string;
  /** Unit PDK reported for this person (standard units or the configured custom field). */
  pdkUnitName: string | null;
  pdkFetchStatus: 'idle' | 'loading' | 'done' | 'empty' | 'error';
  pdkFetchError?: string;
  /** Email filled in when PDK's list hid it and a later GET returned one. */
  emailOverride?: string;
  /** Phone filled in when a later GET returned one the preview did not have. */
  phoneOverride?: string;
};

type Props = {
  open: boolean;
  mode: 'enable' | 'disable';
  integrationId: string | null;
  preview: PdkReconcilePreview | null;
  loading?: boolean;
  submitting?: boolean;
  apartments: ApartmentLite[];
  buildings: BuildingLite[];
  onClose: () => void;
  onConfirm: (payload: {
    selections: {
      create: Array<{
        holderId: string;
        apply: boolean;
        apartmentId: string;
        buildingId: string;
        unitNumber: string;
      }>;
      update: Array<{holderId: string; apply: boolean}>;
      delete: Array<{holderId: string; apply: boolean}>;
    };
  }) => void;
};

const PDK_UNIT_FETCH_BATCH = 15;

/** Step 1 buckets. A person needs an email to be created; the unit can be added in step 2. */
type PeopleFilter = 'all' | 'ready' | 'no_unit' | 'no_email' | 'load_failed';
/** Independent of PeopleFilter: a phone is optional and can sit on any of those buckets. */
type PhoneFilter = 'all' | 'has_phone' | 'no_phone';

function displayName(first?: string | null, last?: string | null, email?: string | null) {
  const n = [first, last].filter(Boolean).join(' ').trim();
  return n || email || '—';
}

function emailOf(row: PdkReconcileCreateRow, draft?: CreateDraft) {
  return (draft?.emailOverride || row.email || '').trim();
}

function phoneOf(row: PdkReconcileCreateRow, draft?: CreateDraft) {
  return (draft?.phoneOverride || row.phone || '').trim();
}

function pdkUnitOf(row: PdkReconcileCreateRow, draft?: CreateDraft) {
  return (draft?.pdkUnitName || row.pdkUnitName || '').trim();
}

/** All PDK units on the row, for the ambiguous "L11 & L12" case. */
function pdkUnitListOf(row: PdkReconcileCreateRow) {
  return (row.pdkUnits || []).filter(u => u.name && !u.moveOut).map(u => u.name);
}

function loadFailed(row: PdkReconcileCreateRow, draft?: CreateDraft) {
  if (draft?.pdkFetchStatus === 'error') return true;
  if (!row.loadError) return false;
  return draft?.pdkFetchStatus !== 'done' && draft?.pdkFetchStatus !== 'empty';
}

function hasPdkUnit(row: PdkReconcileCreateRow, draft?: CreateDraft) {
  return Boolean(pdkUnitOf(row, draft)) || pdkUnitListOf(row).length > 0;
}

/** Step 2 is only for people PDK already gave an email and a unit. Phone is optional. */
function canProceed(row: PdkReconcileCreateRow, draft?: CreateDraft) {
  return Boolean(emailOf(row, draft)) && hasPdkUnit(row, draft) && !loadFailed(row, draft);
}

function proceedBlockReason(row: PdkReconcileCreateRow, draft?: CreateDraft) {
  if (!emailOf(row, draft)) return 'No email in PDK — cannot create this person';
  if (loadFailed(row, draft)) return 'PDK details failed to load — retry before continuing';
  if (!hasPdkUnit(row, draft)) return 'No PDK unit — cannot assign a location';
  return '';
}

/** Apartment, building, and unit are all set, so this row can be created. */
function locationComplete(draft?: CreateDraft) {
  return Boolean(draft?.apartmentId && draft?.buildingId && (draft.unitNumber || '').trim());
}

export default function PdkReconcileModal({
  open,
  mode,
  integrationId,
  preview,
  loading = false,
  submitting = false,
  apartments,
  buildings,
  onClose,
  onConfirm,
}: Props) {
  const [step, setStep] = useState<0 | 1>(0);
  const [createDrafts, setCreateDrafts] = useState<Record<string, CreateDraft>>({});
  const [updateApply, setUpdateApply] = useState<Record<string, boolean>>({});
  const [deleteApply, setDeleteApply] = useState<Record<string, boolean>>({});
  const [roomsByBuildingId, setRoomsByBuildingId] = useState<Record<string, string[]>>({});
  const [loadingRoomsFor, setLoadingRoomsFor] = useState<Set<string>>(new Set());
  const [retryLoading, setRetryLoading] = useState(false);
  const [peopleFilter, setPeopleFilter] = useState<PeopleFilter>('all');
  const [phoneFilter, setPhoneFilter] = useState<PhoneFilter>('all');
  const [batchApartmentId, setBatchApartmentId] = useState('');
  const [batchBuildingId, setBatchBuildingId] = useState('');

  useEffect(() => {
    if (!preview) return;
    const nextCreates: Record<string, CreateDraft> = {};
    for (const row of preview.creates) {
      const matched = row.unitMatchStatus === 'matched';
      const unit = (row.pdkUnitName || '').trim();
      const email = (row.email || '').trim();
      const hasUnit =
        Boolean(unit) || (row.pdkUnits || []).some(u => u.name && !u.moveOut);
      nextCreates[row.holderId] = {
        // Only people with an email and a PDK unit, whose details loaded.
        apply: Boolean(email && hasUnit && !row.loadError),
        apartmentId: matched ? row.defaultApartmentId || '' : '',
        buildingId: matched ? row.defaultBuildingId || '' : '',
        // Always carry the PDK unit into the field; the admin only needs to add the building.
        unitNumber: matched ? row.defaultUnitNumber || unit : unit,
        pdkUnitName: unit || null,
        pdkFetchStatus:
          row.unitMatchStatus === 'unit_unknown' ? 'idle' : unit ? 'done' : 'empty',
      };
    }
    setCreateDrafts(nextCreates);

    const nextUpdates: Record<string, boolean> = {};
    for (const row of preview.updates) nextUpdates[row.holderId] = true;
    setUpdateApply(nextUpdates);

    const nextDeletes: Record<string, boolean> = {};
    for (const row of preview.deletes) nextDeletes[row.holderId] = true;
    setDeleteApply(nextDeletes);

    setRoomsByBuildingId({});
    setLoadingRoomsFor(new Set());
    setPeopleFilter('all');
    setPhoneFilter('all');
    setBatchApartmentId('');
    setBatchBuildingId('');
    setStep(0);
  }, [preview]);

  const buildingsFor = (apartmentId: string, selectedBuildingId: string) => {
    const haveApartmentField = buildings.some(b => b.apartmentId);
    if (!apartmentId || !haveApartmentField) return buildings;
    const scoped = buildings.filter(b => b.apartmentId === apartmentId);
    if (selectedBuildingId && !scoped.some(b => b.id === selectedBuildingId)) {
      const extra = buildings.find(b => b.id === selectedBuildingId);
      return extra ? [extra, ...scoped] : scoped;
    }
    return scoped;
  };

  const fetchRoomsForBuilding = useCallback(
    async (buildingId: string) => {
      if (!buildingId) return;
      if (buildingId in roomsByBuildingId) return;
      if (loadingRoomsFor.has(buildingId)) return;
      setLoadingRoomsFor(prev => new Set(prev).add(buildingId));
      try {
        const res = await roomServiceFind(buildingId, undefined, 1000, 1);
        const data = (res as unknown as {data?: {rooms?: Array<{name?: string}>}}).data;
        const names = Array.isArray(data?.rooms)
          ? data!.rooms!.map(r => r?.name).filter((n): n is string => Boolean(n))
          : [];
        setRoomsByBuildingId(prev => ({...prev, [buildingId]: names}));
      } catch (e) {
        console.warn(`Failed to load rooms for building ${buildingId}`, e);
        setRoomsByBuildingId(prev => ({...prev, [buildingId]: []}));
      } finally {
        setLoadingRoomsFor(prev => {
          const next = new Set(prev);
          next.delete(buildingId);
          return next;
        });
      }
    },
    [roomsByBuildingId, loadingRoomsFor]
  );

  const patchCreate = (holderId: string, patch: Partial<CreateDraft>) => {
    setCreateDrafts(prev => {
      const cur = prev[holderId];
      if (!cur) return prev;
      return {...prev, [holderId]: {...cur, ...patch}};
    });
  };

  /** Apply one PDK detail result to a draft without clobbering what the admin typed. */
  const applyDetail = (
    cur: CreateDraft,
    detail: {unitName?: string; email?: string; phone?: string} | undefined,
    failMsg: string | undefined
  ): CreateDraft => {
    if (!detail) {
      return {...cur, pdkFetchStatus: 'error', pdkFetchError: failMsg || 'Failed to load from PDK'};
    }
    const unitName = (detail.unitName || '').trim();
    const email = (detail.email || '').trim();
    const phone = (detail.phone || '').trim();
    return {
      ...cur,
      pdkUnitName: unitName || null,
      unitNumber: cur.unitNumber.trim() ? cur.unitNumber : unitName,
      pdkFetchStatus: unitName ? 'done' : 'empty',
      pdkFetchError: undefined,
      ...(email ? {emailOverride: email} : {}),
      ...(phone ? {phoneOverride: phone} : {}),
    };
  };

  const retryLoad = async (holderIds: string[]) => {
    if (!integrationId || holderIds.length === 0) return;
    const ids = holderIds.slice(0, PDK_UNIT_FETCH_BATCH);
    setRetryLoading(true);
    for (const id of ids) patchCreate(id, {pdkFetchStatus: 'loading', pdkFetchError: undefined});
    try {
      const res = await acsIntegrationClient.getPdkHolderDetails(integrationId, ids);
      const byId = new Map((res.data.data?.holders || []).map(h => [h.externalHolderId, h]));
      const failById = new Map(
        (res.data.data?.failures || []).map(f => [f.externalHolderId, f.message])
      );
      setCreateDrafts(prev => {
        const next = {...prev};
        for (const id of ids) {
          const cur = next[id];
          if (!cur) continue;
          next[id] = applyDetail(cur, byId.get(id), failById.get(id));
        }
        return next;
      });
      const failed = ids.filter(id => !byId.has(id)).length;
      const remaining = holderIds.length - ids.length;
      if (failed) message.warning(`${failed} of ${ids.length} still could not be loaded from PDK.`);
      else if (remaining > 0) message.info(`Loaded ${ids.length}. ${remaining} left — click again.`);
      else message.success(`Loaded ${ids.length} from PDK.`);
    } catch (e: unknown) {
      const err = e as {response?: {data?: {message?: string}}};
      message.error(err?.response?.data?.message || 'Failed to load from PDK');
      setCreateDrafts(prev => {
        const next = {...prev};
        for (const id of ids) {
          if (next[id]?.pdkFetchStatus === 'loading') {
            next[id] = {...next[id], pdkFetchStatus: 'error', pdkFetchError: 'Failed to load from PDK'};
          }
        }
        return next;
      });
    } finally {
      setRetryLoading(false);
    }
  };

  // ---------- Step 1 derived state ----------

  const bucketOf = (row: PdkReconcileCreateRow): Exclude<PeopleFilter, 'all'> => {
    const d = createDrafts[row.holderId];
    if (!emailOf(row, d)) return 'no_email';
    if (loadFailed(row, d) && !pdkUnitOf(row, d)) return 'load_failed';
    if (!pdkUnitOf(row, d) && pdkUnitListOf(row).length === 0) return 'no_unit';
    return 'ready';
  };

  const bucketCounts = useMemo(() => {
    const c = {all: 0, ready: 0, no_unit: 0, no_email: 0, load_failed: 0};
    if (!preview) return c;
    c.all = preview.creates.length;
    for (const row of preview.creates) c[bucketOf(row)] += 1;
    return c;
  }, [preview, createDrafts]);

  const peopleInBucket = useMemo(() => {
    if (!preview) return [];
    if (peopleFilter === 'all') return preview.creates;
    return preview.creates.filter(row => bucketOf(row) === peopleFilter);
  }, [preview, createDrafts, peopleFilter]);

  const phoneCounts = useMemo(() => {
    const c = {has_phone: 0, no_phone: 0};
    for (const row of peopleInBucket) {
      if (phoneOf(row, createDrafts[row.holderId])) c.has_phone += 1;
      else c.no_phone += 1;
    }
    return c;
  }, [peopleInBucket, createDrafts]);

  const visiblePeople = useMemo(() => {
    if (phoneFilter === 'all') return peopleInBucket;
    return peopleInBucket.filter(row => {
      const has = Boolean(phoneOf(row, createDrafts[row.holderId]));
      return phoneFilter === 'has_phone' ? has : !has;
    });
  }, [peopleInBucket, createDrafts, phoneFilter]);

  const selectedPeople = useMemo(
    () => (preview ? preview.creates.filter(row => createDrafts[row.holderId]?.apply) : []),
    [preview, createDrafts]
  );

  const failedIds = useMemo(
    () =>
      preview
        ? preview.creates
            .filter(row => loadFailed(row, createDrafts[row.holderId]))
            .map(row => row.holderId)
        : [],
    [preview, createDrafts]
  );

  const setPeople = (rows: PdkReconcileCreateRow[], apply: boolean) => {
    setCreateDrafts(prev => {
      const next = {...prev};
      for (const row of rows) {
        const cur = next[row.holderId];
        if (!cur) continue;
        next[row.holderId] = {...cur, apply: apply && canProceed(row, cur)};
      }
      return next;
    });
  };

  const peopleSelectState = useMemo(() => {
    const selectable = visiblePeople.filter(row => canProceed(row, createDrafts[row.holderId]));
    if (selectable.length === 0) return {checked: false, indeterminate: false};
    const on = selectable.filter(row => createDrafts[row.holderId]?.apply).length;
    return {checked: on === selectable.length, indeterminate: on > 0 && on < selectable.length};
  }, [visiblePeople, createDrafts]);

  // ---------- Step 2 derived state ----------

  const incompleteCount = useMemo(
    () => selectedPeople.filter(row => !locationComplete(createDrafts[row.holderId])).length,
    [selectedPeople, createDrafts]
  );

  const unassigned = useMemo(
    () =>
      selectedPeople.filter(row => {
        const d = createDrafts[row.holderId];
        return !d?.apartmentId || !d?.buildingId;
      }),
    [selectedPeople, createDrafts]
  );

  /** Is this unit already a room in the chosen building? Unknown until that building's rooms load. */
  const roomExists = (draft: CreateDraft): boolean | null => {
    const unit = draft.unitNumber.trim().toLowerCase();
    if (!draft.buildingId || !unit) return null;
    const rooms = roomsByBuildingId[draft.buildingId];
    if (!rooms) return null;
    return rooms.some(r => r.trim().toLowerCase() === unit);
  };

  const newRoomCount = useMemo(
    () =>
      selectedPeople.filter(row => {
        const d = createDrafts[row.holderId];
        return d && locationComplete(d) && roomExists(d) === false;
      }).length,
    [selectedPeople, createDrafts, roomsByBuildingId]
  );

  const applyBatch = (targets: PdkReconcileCreateRow[]) => {
    if (!batchApartmentId || !batchBuildingId || targets.length === 0) return;
    setCreateDrafts(prev => {
      const next = {...prev};
      for (const row of targets) {
        const cur = next[row.holderId];
        if (!cur) continue;
        next[row.holderId] = {
          ...cur,
          apartmentId: batchApartmentId,
          buildingId: batchBuildingId,
          unitNumber: cur.unitNumber.trim() || pdkUnitOf(row, cur),
        };
      }
      return next;
    });
    void fetchRoomsForBuilding(batchBuildingId);
    message.success(`Assigned ${targets.length} ${targets.length === 1 ? 'person' : 'people'}.`);
  };

  const readyPeople = useMemo(
    () => selectedPeople.filter(row => canProceed(row, createDrafts[row.holderId])),
    [selectedPeople, createDrafts]
  );

  const filterBlocksNext =
    peopleFilter === 'no_email' || peopleFilter === 'no_unit' || peopleFilter === 'load_failed';

  const canGoNext = readyPeople.length > 0 && !filterBlocksNext;

  const goToStep2 = () => {
    if (!canGoNext) return;
    // Preload rooms for buildings already chosen by matched rows.
    const ids = new Set<string>();
    for (const row of readyPeople) {
      const b = createDrafts[row.holderId]?.buildingId;
      if (b) ids.add(b);
    }
    ids.forEach(id => void fetchRoomsForBuilding(id));
    setStep(1);
  };

  const handleConfirm = () => {
    if (!preview) return;
    if (mode === 'enable' && incompleteCount > 0) {
      message.error(
        `${incompleteCount} selected ${incompleteCount === 1 ? 'person still needs' : 'people still need'} an apartment, building, and unit.`
      );
      return;
    }
    onConfirm({
      selections: {
        create: preview.creates.map(row => {
          const d = createDrafts[row.holderId];
          return {
            holderId: row.holderId,
            apply: Boolean(d?.apply),
            apartmentId: d?.apartmentId || '',
            buildingId: d?.buildingId || '',
            unitNumber: (d?.unitNumber || '').trim(),
          };
        }),
        update: preview.updates.map(row => ({
          holderId: row.holderId,
          apply: Boolean(updateApply[row.holderId]),
        })),
        delete: preview.deletes.map(row => ({
          holderId: row.holderId,
          apply: Boolean(deleteApply[row.holderId]),
        })),
      },
    });
  };

  // ---------- Renderers ----------

  const sourceLabel = (row: PdkReconcileCreateRow) =>
    row.pdkUnitSource === 'custom_field' && preview?.unitCustomField
      ? `PDK field “${preview.unitCustomField}”`
      : 'PDK unit';

  const unitChip = (row: PdkReconcileCreateRow, draft?: CreateDraft) => {
    if (draft?.pdkFetchStatus === 'loading') {
      return <Chip size="small" icon={<CircularProgress size={12} />} label="Loading…" />;
    }
    const units = pdkUnitListOf(row);
    if (units.length > 1 && !pdkUnitOf(row, draft)) {
      return (
        <Tooltip title={`${sourceLabel(row)} lists ${units.join(', ')}. Pick one in the next step.`}>
          <Chip size="small" color="warning" variant="outlined" label={`${units.length} units`} />
        </Tooltip>
      );
    }
    const unit = pdkUnitOf(row, draft);
    if (unit) {
      const matched = row.unitMatchStatus === 'matched';
      return (
        <Tooltip
          title={
            matched
              ? `${sourceLabel(row)} “${unit}” matches an existing Entrio room.`
              : `${sourceLabel(row)} “${unit}”. No Entrio room has this name yet; it will be created in the building you choose.`
          }
        >
          <Chip
            size="small"
            color={matched ? 'success' : 'default'}
            variant={matched ? 'filled' : 'outlined'}
            label={matched ? `${unit} · existing room` : unit}
          />
        </Tooltip>
      );
    }
    if (loadFailed(row, draft)) {
      return (
        <Tooltip title={draft?.pdkFetchError || row.loadError || 'PDK request failed'}>
          <Chip size="small" color="error" variant="outlined" label="Load failed" />
        </Tooltip>
      );
    }
    return (
      <Tooltip
        title={
          preview?.unitCustomField
            ? `PDK has no current unit and “${preview.unitCustomField}” is empty.`
            : 'PDK has no current unit for this person.'
        }
      >
        <Chip size="small" variant="outlined" label="No unit" />
      </Tooltip>
    );
  };

  const renderPersonRow = (row: PdkReconcileCreateRow) => {
    const draft = createDrafts[row.holderId];
    const email = emailOf(row, draft);
    const canSelect = canProceed(row, draft);
    const failed = loadFailed(row, draft);
    return (
      <Stack
        key={row.holderId}
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{py: 0.75, borderBottom: '1px solid', borderColor: 'divider'}}
      >
        <Tooltip title={proceedBlockReason(row, draft)}>
          <span>
            <Checkbox
              size="small"
              checked={Boolean(draft?.apply)}
              disabled={!canSelect || submitting}
              onChange={e => patchCreate(row.holderId, {apply: e.target.checked})}
            />
          </span>
        </Tooltip>
        <Box sx={{flex: 1, minWidth: 0}}>
          <Typography variant="body2" noWrap>
            {displayName(row.firstName, row.lastName, email)}
          </Typography>
          {email ? (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {email}
            </Typography>
          ) : (
            <Typography variant="caption" color="warning.main" display="block">
              No email in PDK — cannot create
            </Typography>
          )}
        </Box>
        <Box sx={{width: 150, flexShrink: 0, textAlign: 'right'}}>
          {phoneOf(row, draft) ? (
            <Typography variant="caption" color="text.secondary" noWrap>
              {phoneOf(row, draft)}
            </Typography>
          ) : (
            <Chip size="small" variant="outlined" label="No phone" />
          )}
        </Box>
        <Box sx={{minWidth: 140, textAlign: 'right'}}>{unitChip(row, draft)}</Box>
        {failed && (
          <Button
            size="small"
            disabled={submitting || retryLoading || !integrationId}
            onClick={() => void retryLoad([row.holderId])}
          >
            Retry
          </Button>
        )}
      </Stack>
    );
  };

  const renderLocationRow = (row: PdkReconcileCreateRow) => {
    const draft = createDrafts[row.holderId];
    if (!draft) return null;
    const bOpts = buildingsFor(draft.apartmentId, draft.buildingId);
    const roomOptions = draft.buildingId ? roomsByBuildingId[draft.buildingId] || [] : [];
    const matched = row.unitMatchStatus === 'matched';
    const unitMissing = !draft.unitNumber.trim();
    const buildingMissing = !draft.apartmentId || !draft.buildingId;
    const exists = roomExists(draft);
    const units = pdkUnitListOf(row);

    let unitHelp: string;
    if (unitMissing) {
      unitHelp =
        units.length > 1
          ? `${sourceLabel(row)} lists ${units.join(', ')} — pick one`
          : 'Type or pick a unit';
    } else if (buildingMissing) {
      unitHelp = `${sourceLabel(row)} — choose the building`;
    } else if (matched && exists !== false) {
      unitHelp = 'Existing room';
    } else if (exists === true) {
      unitHelp = 'Existing room in this building';
    } else if (exists === false) {
      unitHelp = 'New room — created on confirm';
    } else {
      unitHelp = sourceLabel(row);
    }

    return (
      <Box key={row.holderId} sx={{py: 1.25, borderBottom: '1px solid', borderColor: 'divider'}}>
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{mb: 0.75}}>
          <Typography variant="body2" fontWeight={500}>
            {displayName(row.firstName, row.lastName, emailOf(row, draft))}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {emailOf(row, draft)}
            {phoneOf(row, draft) ? ` · ${phoneOf(row, draft)}` : ' · no phone'}
          </Typography>
          {locationComplete(draft) ? (
            <Chip size="small" color="success" variant="outlined" label="Ready" />
          ) : (
            <Chip size="small" color="warning" variant="outlined" label="Needs location" />
          )}
        </Stack>
        <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} alignItems="flex-start">
          <FormControl size="small" sx={{minWidth: 170}} disabled={submitting}>
            <InputLabel>Apartment</InputLabel>
            <Select
              label="Apartment"
              value={draft.apartmentId}
              onChange={e => {
                const apartmentId = String(e.target.value);
                const keep = buildingsFor(apartmentId, draft.buildingId).some(
                  b => b.id === draft.buildingId
                );
                patchCreate(row.holderId, {apartmentId, buildingId: keep ? draft.buildingId : ''});
              }}
            >
              {apartments.map(a => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            sx={{minWidth: 170}}
            disabled={submitting || !draft.apartmentId}
            error={Boolean(draft.apartmentId) && !draft.buildingId}
          >
            <InputLabel>Building</InputLabel>
            <Select
              label="Building"
              value={draft.buildingId}
              onChange={e => {
                const buildingId = String(e.target.value);
                patchCreate(row.holderId, {buildingId});
                void fetchRoomsForBuilding(buildingId);
              }}
              onOpen={() => {
                if (draft.buildingId) void fetchRoomsForBuilding(draft.buildingId);
              }}
            >
              {bOpts.map(b => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            size="small"
            freeSolo
            disabled={submitting}
            options={units.length > 1 && !draft.buildingId ? units : roomOptions}
            loading={loadingRoomsFor.has(draft.buildingId)}
            value={draft.unitNumber}
            onOpen={() => {
              if (draft.buildingId) void fetchRoomsForBuilding(draft.buildingId);
            }}
            onInputChange={(_, v) => patchCreate(row.holderId, {unitNumber: v || ''})}
            sx={{minWidth: 180, flex: 1}}
            renderInput={params => (
              <TextField
                {...params}
                label="Unit"
                error={unitMissing}
                helperText={unitHelp}
                FormHelperTextProps={
                  exists === false && !unitMissing && !buildingMissing
                    ? {sx: {color: 'info.main'}}
                    : undefined
                }
              />
            )}
          />
        </Stack>
      </Box>
    );
  };

  const renderUpdateRow = (row: PdkReconcileUpdateRow) => (
    <Box key={row.holderId} sx={{py: 1, borderBottom: '1px solid', borderColor: 'divider'}}>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={Boolean(updateApply[row.holderId])}
            onChange={e => setUpdateApply(prev => ({...prev, [row.holderId]: e.target.checked}))}
          />
        }
        label={
          <Box>
            <Typography variant="body2">
              {displayName(row.pdk.firstName, row.pdk.lastName, row.pdk.email)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Entrio: {displayName(row.entrio.firstName, row.entrio.lastName, row.entrio.email)}
              {row.entrio.email ? ` · ${row.entrio.email}` : ''}
              {row.entrio.phone ? ` · ${row.entrio.phone}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              PDK: {displayName(row.pdk.firstName, row.pdk.lastName, row.pdk.email)}
              {row.pdk.email ? ` · ${row.pdk.email}` : ''}
              {row.pdk.phone ? ` · ${row.pdk.phone}` : ''}
            </Typography>
            {row.unitMove && (
              <Typography variant="caption" color="text.secondary" display="block">
                Unit: {row.unitMove.fromBuildingName || '—'} / {row.unitMove.fromRoomName || '—'} →{' '}
                {row.unitMove.toBuildingName || '—'} / {row.unitMove.toRoomName || '—'}
              </Typography>
            )}
          </Box>
        }
      />
    </Box>
  );

  const renderDeleteRow = (row: PdkReconcileDeleteRow) => (
    <Box key={row.holderId} sx={{py: 1, borderBottom: '1px solid', borderColor: 'divider'}}>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={Boolean(deleteApply[row.holderId])}
            onChange={e => setDeleteApply(prev => ({...prev, [row.holderId]: e.target.checked}))}
          />
        }
        label={
          <Typography variant="body2">
            {displayName(row.firstName, row.lastName, row.email)}
            {row.email ? (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ml: 1}}>
                {row.email}
              </Typography>
            ) : null}
          </Typography>
        }
      />
    </Box>
  );

  const simpleSection = (
    label: string,
    rows: Array<{holderId: string}>,
    applyMap: Record<string, boolean>,
    setApplyMap: (fn: (prev: Record<string, boolean>) => Record<string, boolean>) => void,
    render: (row: never) => ReactNode,
    disabledNote?: string
  ) => {
    const on = rows.filter(r => applyMap[r.holderId]).length;
    const setAll = (apply: boolean) =>
      setApplyMap(prev => {
        const next = {...prev};
        for (const r of rows) next[r.holderId] = apply;
        return next;
      });
    return (
      <>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mt: 2, mb: 0.5}}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {!disabledNote && rows.length > 0 && (
              <Checkbox
                size="small"
                checked={on === rows.length}
                indeterminate={on > 0 && on < rows.length}
                disabled={submitting}
                onChange={e => setAll(e.target.checked)}
              />
            )}
            <Typography variant="subtitle2" fontWeight={600}>
              {label} ({rows.length})
            </Typography>
          </Stack>
        </Stack>
        {disabledNote ? (
          <Alert severity="info">{disabledNote}</Alert>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            None
          </Typography>
        ) : (
          rows.map(r => render(r as never))
        )}
      </>
    );
  };

  // ---------- Layout ----------

  const title =
    mode === 'enable'
      ? 'Enable PDK auto-sync'
      : 'Review differences before disabling auto-sync';

  const filterButtons: Array<[PeopleFilter, string]> = [
    ['all', 'All'],
    ['ready', 'Email + unit'],
    ['no_unit', 'No unit'],
    ['no_email', 'No email'],
    ['load_failed', 'Load failed'],
  ];

  const step1 = preview && (
    <>
      <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
        {preview.pdkHolderCount} people in PDK · {preview.entrioActiveLinkCount} already linked in
        Entrio. Only people with both an email and a PDK unit can continue. A missing phone
        does not block them. People with no email, no unit, or a failed PDK load stay on this step.
      </Typography>
      {preview.warnings?.map(w => (
        <Alert key={w} severity="warning" sx={{mb: 1}}>
          {w}
        </Alert>
      ))}
      {bucketCounts.load_failed > 0 && (
        <Alert
          severity="error"
          sx={{mb: 1}}
          action={
            <Button
              color="inherit"
              size="small"
              disabled={retryLoading || !integrationId}
              onClick={() => void retryLoad(failedIds)}
              startIcon={retryLoading ? <CircularProgress size={12} color="inherit" /> : undefined}
            >
              Retry {Math.min(failedIds.length, PDK_UNIT_FETCH_BATCH)}
            </Button>
          }
        >
          PDK did not return details for {bucketCounts.load_failed}{' '}
          {bucketCounts.load_failed === 1 ? 'person' : 'people'}, so their unit is unknown.
        </Alert>
      )}
      <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1, flexWrap: 'wrap', rowGap: 1}}>
        {filterButtons.map(([id, label]) => (
          <Button
            key={id}
            size="small"
            variant={peopleFilter === id ? 'contained' : 'outlined'}
            onClick={() => setPeopleFilter(id)}
            disabled={submitting}
          >
            {label} ({bucketCounts[id]})
          </Button>
        ))}
        <Box sx={{width: 8}} />
        {(
          [
            ['has_phone', 'Has phone'],
            ['no_phone', 'No phone'],
          ] as Array<[Exclude<PhoneFilter, 'all'>, string]>
        ).map(([id, label]) => (
          <Button
            key={id}
            size="small"
            variant={phoneFilter === id ? 'contained' : 'outlined'}
            onClick={() => setPhoneFilter(phoneFilter === id ? 'all' : id)}
            disabled={submitting}
          >
            {label} ({phoneCounts[id]})
          </Button>
        ))}
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 0.5}}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Checkbox
            size="small"
            checked={peopleSelectState.checked}
            indeterminate={peopleSelectState.indeterminate}
            disabled={submitting || visiblePeople.length === 0}
            onChange={e => setPeople(visiblePeople, e.target.checked)}
          />
          <Typography variant="subtitle2" fontWeight={600}>
            {peopleFilter === 'all' ? 'People' : filterButtons.find(f => f[0] === peopleFilter)?.[1]}{' '}
            ({visiblePeople.length}) ·{' '}
            {visiblePeople.filter(row => createDrafts[row.holderId]?.apply).length} selected
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2} sx={{pr: 1}}>
          <Typography variant="caption" color="text.secondary" sx={{width: 150, textAlign: 'right'}}>
            Phone
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{minWidth: 140, textAlign: 'right'}}>
            PDK unit
          </Typography>
        </Stack>
      </Stack>
      {visiblePeople.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          None in this filter
        </Typography>
      ) : (
        visiblePeople.map(renderPersonRow)
      )}
    </>
  );

  const step2 = preview && (
    <>
      <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
        Assign an apartment and building to each selected person. Units already matching an
        Entrio room are filled in. Any other unit is created as a new room in the chosen building
        when you confirm.
      </Typography>
      {selectedPeople.length > 0 && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            bgcolor: 'action.hover',
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} sx={{mb: 1}}>
            Batch assign
          </Typography>
          <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{minWidth: 180}} disabled={submitting}>
              <InputLabel>Apartment</InputLabel>
              <Select
                label="Apartment"
                value={batchApartmentId}
                onChange={e => {
                  const id = String(e.target.value);
                  setBatchApartmentId(id);
                  if (!buildingsFor(id, '').some(b => b.id === batchBuildingId)) setBatchBuildingId('');
                }}
              >
                {apartments.map(a => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{minWidth: 180}} disabled={submitting || !batchApartmentId}>
              <InputLabel>Building</InputLabel>
              <Select
                label="Building"
                value={batchBuildingId}
                onChange={e => setBatchBuildingId(String(e.target.value))}
              >
                {buildingsFor(batchApartmentId, '').map(b => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="contained"
              disabled={submitting || !batchApartmentId || !batchBuildingId || unassigned.length === 0}
              onClick={() => applyBatch(unassigned)}
            >
              Apply to {unassigned.length} unassigned
            </Button>
            <Button
              size="small"
              disabled={submitting || !batchApartmentId || !batchBuildingId}
              onClick={() => applyBatch(selectedPeople)}
            >
              Apply to all {selectedPeople.length}
            </Button>
          </Stack>
        </Box>
      )}
      {selectedPeople.length > 0 && (
        <Stack direction="row" spacing={1} sx={{mb: 1}} flexWrap="wrap">
          <Chip
            size="small"
            color={incompleteCount === 0 ? 'success' : 'warning'}
            label={
              incompleteCount === 0
                ? `All ${selectedPeople.length} ready`
                : `${incompleteCount} of ${selectedPeople.length} still need a location`
            }
          />
          {newRoomCount > 0 && (
            <Chip size="small" variant="outlined" label={`${newRoomCount} new room${newRoomCount === 1 ? '' : 's'} will be created`} />
          )}
        </Stack>
      )}
      {selectedPeople.length === 0 ? (
        <Alert severity="info" sx={{mb: 1}}>
          Nobody selected. Confirming only turns on auto-sync for future PDK changes.
        </Alert>
      ) : (
        selectedPeople.map(renderLocationRow)
      )}

      <Divider sx={{my: 2}} />
      {simpleSection(
        'Also update in Entrio',
        preview.updates,
        updateApply,
        setUpdateApply,
        renderUpdateRow as (row: never) => ReactNode
      )}
      {!preview.deletesDisabled &&
        simpleSection(
          'Delete from Entrio (no longer in PDK)',
          preview.deletes,
          deleteApply,
          setDeleteApply,
          renderDeleteRow as (row: never) => ReactNode
        )}
    </>
  );

  const disableView = preview && (
    <>
      <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
        {preview.pdkHolderCount} people in PDK · {preview.entrioActiveLinkCount} linked in Entrio.
      </Typography>
      {preview.warnings?.map(w => (
        <Alert key={w} severity="warning" sx={{mb: 1}}>
          {w}
        </Alert>
      ))}
      {simpleSection(
        'Delete from Entrio (no longer in PDK)',
        preview.deletes,
        deleteApply,
        setDeleteApply,
        renderDeleteRow as (row: never) => ReactNode,
        preview.deletesDisabled
          ? 'Delete suggestions are disabled because the PDK list failed or returned empty while Entrio still has linked residents.'
          : undefined
      )}
    </>
  );

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{pb: mode === 'enable' ? 0 : undefined}}>
        {title}
        {mode === 'enable' && (
          <Stepper activeStep={step} sx={{mt: 1.5, mb: 0.5}}>
            <Step>
              <StepLabel>Select people</StepLabel>
            </Step>
            <Step>
              <StepLabel>Assign apartment &amp; building</StepLabel>
            </Step>
          </Stepper>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading && <Typography color="text.secondary">Comparing PDK and Entrio…</Typography>}
        {!loading && preview && (mode === 'disable' ? disableView : step === 0 ? step1 : step2)}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        {mode === 'enable' && step === 1 && (
          <Button onClick={() => setStep(0)} disabled={submitting}>
            Back
          </Button>
        )}
        <Box sx={{flex: 1}} />
        {mode === 'enable' && step === 0 ? (
          <Tooltip
            title={
              canGoNext
                ? ''
                : filterBlocksNext
                  ? 'Switch back to All or Email + unit. This list cannot be assigned a location.'
                  : 'Select people who have both an email and a PDK unit.'
            }
          >
            <span>
              <Button
                variant="contained"
                onClick={goToStep2}
                disabled={loading || submitting || !preview || !canGoNext}
              >
                {readyPeople.length > 0
                  ? `Next: assign locations (${readyPeople.length})`
                  : 'Next: assign locations'}
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={loading || submitting || !preview || (mode === 'enable' && incompleteCount > 0)}
          >
            {submitting
              ? 'Working…'
              : mode === 'enable'
                ? selectedPeople.length > 0
                  ? `Create ${selectedPeople.length} & enable`
                  : 'Enable auto-sync'
                : 'Confirm & disable'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
